import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  MenuItem, Box, Checkbox, FormControlLabel, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import { C, fmtINR } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdDateField from "../../ipd/utils/IpdDateField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import IpdSectionCard from "../../ipd/utils/IpdSectionCard";
import IpdDataGrid from "../../ipd/utils/IpdDataGrid";
import SvgIcon from "../SvgIcon";
import {
  listCorporatePayers,
  getRateCard,
  createRateCard,
  updateRateCard,
  listRateCardItems,
  bulkSaveRateCardItems,
  importRateCardItems,
  getImportTemplate,
  updateRateCardItem,
  deleteRateCardItem,
} from "../corporate-api";
import {
  listServiceGroups,
  listServiceClassifications,
  listMedicalServices,
  listMedicalServicesForGroup,
  toBillingError,
} from "../billing-api";
import { listLookups } from "../service-master-api";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Format an ISO date (YYYY-MM-DD) as "DD MMM YYYY"; blank renders as an em-dash.
const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// ── Header form ───────────────────────────────────────────────────────────────
// rate_card_type / rate_card_status / priority are no longer editable here —
// they stay in form state because the API still requires them, and an edit must
// round-trip the server's existing values rather than reset them to defaults.
function HeaderForm({ rateCardId, initialPayer, payers, onSaved }) {
  const [form, setForm] = useState({
    payer_id: initialPayer?.id || "",
    rate_card_code: "",
    rate_card_name: "",
    rate_card_type: "MASTER",
    encounter_type: "IPD",
    rate_card_status: "ACTIVE",
    priority: 100,
    effective_from: todayISO(),
    effective_to: "",
  });
  const [loading, setLoading] = useState(Boolean(rateCardId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(""); // inline load error only; save results toast

  useEffect(() => {
    if (!rateCardId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const c = await getRateCard(rateCardId);
        if (cancelled) return;
        setForm({
          payer_id: c.payerId || "",
          rate_card_code: c.code === "—" ? "" : c.code,
          rate_card_name: c.name === "—" ? "" : c.name,
          rate_card_type: c.type || "MASTER",
          encounter_type: c.encounterType || "IPD",
          rate_card_status: c.status || "ACTIVE",
          priority: c.priority ?? 100,
          effective_from: c.effectiveFrom || todayISO(),
          effective_to: c.effectiveTo || "",
        });
      } catch (err) {
        if (!cancelled) setError(toBillingError(err, "Could not load rate card"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [rateCardId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.payer_id) { toast.error("Select a corporate"); return; }
    if (!form.rate_card_code.trim()) { toast.error("Rate card code is required"); return; }
    setSaving(true); setError("");
    const body = {
      payer_id: form.payer_id,
      rate_card_code: form.rate_card_code.trim(),
      rate_card_name: form.rate_card_name.trim() || form.rate_card_code.trim(),
      rate_card_type: form.rate_card_type,
      encounter_type: form.encounter_type,
      rate_card_status: form.rate_card_status,
      priority: Number(form.priority) || 0,
      effective_from: form.effective_from,
      is_active: true,
    };
    if (form.effective_to) body.effective_to = form.effective_to;
    try {
      const saved = rateCardId ? await updateRateCard(rateCardId, body) : await createRateCard(body);
      toast.success(rateCardId ? "Rate card updated" : "Rate card created");
      if (!rateCardId && saved?.id) onSaved(saved.id, payers.find((p) => p.id === form.payer_id) || initialPayer);
    } catch (err) {
      toast.error(toBillingError(err, "Could not save rate card"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <IpdSectionCard title="Rate card header"><Box sx={{ color: C.textSub, fontSize: "13px" }}>Loading…</Box></IpdSectionCard>;

  return (
    <IpdSectionCard
      title={rateCardId ? "Rate card header" : "New rate card"}
      subtitle="Contract package per corporate payer"
      actions={
        <IpdButton onClick={submit} loading={saving}>
          {rateCardId ? "Save changes" : "Create rate card"}
        </IpdButton>
      }
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px" }}>
        <IpdFormField label="Corporate" required>
          <IpdTextField select fullWidth value={form.payer_id} onChange={set("payer_id")} disabled={Boolean(initialPayer && !rateCardId)}>
            <MenuItem value="">Select corporate…</MenuItem>
            {initialPayer && !payers.some((p) => p.id === initialPayer.id) && (
              <MenuItem value={initialPayer.id}>{initialPayer.name} ({initialPayer.code})</MenuItem>
            )}
            {payers.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name} ({p.code})</MenuItem>
            ))}
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label="Rate card code" required>
          <IpdTextField fullWidth value={form.rate_card_code} onChange={set("rate_card_code")} placeholder="TCS-MASTER-2026" />
        </IpdFormField>
        <IpdFormField label="Rate card name">
          <IpdTextField fullWidth value={form.rate_card_name} onChange={set("rate_card_name")} placeholder="TCS Full Hospital Contract 2026" />
        </IpdFormField>
        <IpdFormField label="Encounter type">
          <IpdTextField select fullWidth value={form.encounter_type} onChange={set("encounter_type")}>
            {["IPD", "OPD", "ALL"].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label="Effective from">
          <IpdDateField value={form.effective_from} onChange={(iso) => set("effective_from")({ target: { value: iso } })} />
        </IpdFormField>
        <IpdFormField label="Effective to (optional)">
          <IpdDateField value={form.effective_to} onChange={(iso) => set("effective_to")({ target: { value: iso } })} />
        </IpdFormField>
      </Box>
      {error && (
        <Box sx={{ marginTop: "14px", fontSize: "13px", fontWeight: 600, color: C.danger }}>
          {error}
        </Box>
      )}
    </IpdSectionCard>
  );
}

// ── Manual line-item grid (spec chain: groups → classifications → services) ───
// GET /service-groups/ → GET /service-classifications/?group_id= →
// GET /service-catalog/services/ (shows rack `fee`). User edits the corporate
// price column across one or more classifications → POST .../items/bulk/.
//
// Rate level maps to the API's scope_type: GROUP → CATEGORY (scope_code =
// group_code), CLASSIFICATION → SUB_CATEGORY (scope_code = classification_code),
// SERVICE → SERVICE_CODE (service_code, prefer unique_code so one rule covers all
// bed tariffs). A group/classification rate is ONE code-based line; the pricing
// resolver matches the most specific scope first, so per-service lines override.
const RATE_LEVELS = [
  { id: "SERVICE", label: "Service" },
  { id: "CLASSIFICATION", label: "Classification" },
  { id: "GROUP", label: "Service group" },
];

const emptyBulk = () => ({
  pricing_method: "PERCENTAGE_DISCOUNT",
  corporate_unit_price: "",
  discount_percentage: "",
  is_excluded: false,
});

function ManualGrid({ rateCardId, effectiveFrom, onSaved }) {
  const [rateLevel, setRateLevel] = useState("SERVICE");
  const [bulk, setBulk] = useState(emptyBulk);
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [classifications, setClassifications] = useState([]);
  const [classificationId, setClassificationId] = useState("");
  const [bedTypes, setBedTypes] = useState([]);
  const [bedTypeCode, setBedTypeCode] = useState("");
  const [sampleTypes, setSampleTypes] = useState([]);
  const [sampleId, setSampleId] = useState("");
  // "This rate applies to" — the bed-type + effective window stamped on EVERY line saved in
  // this batch (group / classification / service). Bed type blank = any bed. The dates must
  // sit inside the rate card's own effective window (the server enforces this).
  const [lineBedType, setLineBedType] = useState("");
  const [lineEffFrom, setLineEffFrom] = useState(effectiveFrom || todayISO());
  const [lineEffTo, setLineEffTo] = useState("");
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  // Priced rows accumulate across group/classification changes, keyed by
  // unique_code when the row has one (shared identity across bed variants),
  // else the raw service_code.
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  // 1) Service groups (once).
  useEffect(() => {
    (async () => {
      try { setGroups(await listServiceGroups()); } catch { /* non-fatal */ }
    })();
  }, []);

  // Bed types + sample types (once) — Service Master lookups, reused here so
  // the picker can narrow a group+classification down to one bed's tariff row
  // instead of listing every bed variant (GB/ICU/PR/SP/ITU) at once.
  useEffect(() => {
    (async () => {
      try {
        const { bedTypes: bt, sampleTypes: st } = await listLookups({ activeOnly: true });
        setBedTypes(bt);
        setSampleTypes(st);
      } catch { /* non-fatal — bed/sample filters just stay empty */ }
    })();
  }, []);

  // A group-level rate covers the whole group, so the narrowing filters below it
  // must not silently shrink what "the group" expands to.
  useEffect(() => {
    setBulk(emptyBulk());
    if (rateLevel === "GROUP") {
      setClassificationId("");
      setBedTypeCode("");
      setSampleId("");
    }
  }, [rateLevel]);

  // 2) Classifications for the selected group.
  useEffect(() => {
    setClassificationId("");
    setClassifications([]);
    if (!groupId) return;
    let cancelled = false;
    (async () => {
      try {
        const cls = await listServiceClassifications(groupId);
        if (!cancelled) setClassifications(cls);
      } catch { if (!cancelled) setClassifications([]); }
    })();
    return () => { cancelled = true; };
  }, [groupId]);

  // 3) Services for the group (+ classification, bed type, sample when chosen) —
  // these populate the per-service exceptions table. The catalog endpoint requires
  // a classification, so at GROUP level we union services across every
  // classification so exclusions/overrides can still be picked (the group rate
  // itself is one CATEGORY line and does not need this list).
  useEffect(() => {
    if (!groupId) { setServices([]); return; }
    let cancelled = false;
    setServicesLoading(true);
    (async () => {
      try {
        const svcs = rateLevel === "GROUP"
          ? await listMedicalServicesForGroup(groupId)
          : await listMedicalServices({
              groupId,
              classificationId: classificationId || undefined,
              bedTypeCode: bedTypeCode || undefined,
              sampleId: sampleId || undefined,
            });
        if (!cancelled) setServices(svcs);
      } catch {
        if (!cancelled) setServices([]);
      } finally {
        if (!cancelled) setServicesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, classificationId, bedTypeCode, sampleId, rateLevel]);

  // Each bed variant of a service is priced independently, so the drafts map is
  // keyed per row (svc.id is unique per bed tariff). The saved scope_code still
  // prefers unique_code (shared across beds) — the per-row bed_type_code below is
  // what makes each ICU/GB/… line distinct at resolve time.
  const draftKey = (svc) => svc.id;

  const blankDraft = (svc) => ({
    service_code: svc.uniqueCode || svc.code,
    raw_service_code: svc.code,
    bed_type_code: svc.bedTypeCode,
    scope_name: svc.name,
    standard_unit_price: svc.fee,
    pricing_method: "FIXED_RATE",
    corporate_unit_price: "",
    discount_percentage: "",
    is_excluded: false,
  });

  const setDraft = (svc, patch) =>
    setDrafts((d) => {
      const key = draftKey(svc);
      return { ...d, [key]: { ...(d[key] || blankDraft(svc)), ...patch } };
    });

  // Turn a {is_excluded, pricing_method, value} shape into the pricing fields of
  // a rate-card item — shared by the group/classification line and per-service
  // exception lines. Excluded rows are now encoded using the explicit EXCLUDE
  // pricing method, while still preserving is_excluded for UI/compatibility.
  const pricingFields = (r) => {
    if (r.is_excluded) {
      return { pricing_method: "EXCLUDE", is_excluded: true };
    }
    if (r.pricing_method === "FIXED_RATE") {
      return { pricing_method: "FIXED_RATE", corporate_unit_price: String(r.corporate_unit_price) };
    }
    return { pricing_method: "PERCENTAGE_DISCOUNT", discount_percentage: String(r.discount_percentage) };
  };

  // A draft counts as "priced" once its method's value is filled — an exclusion
  // needs no value, the flag itself is the instruction.
  const isPriced = (r) =>
    r.is_excluded
      ? true
      : r.pricing_method === "PERCENTAGE_DISCOUNT"
        ? String(r.discount_percentage).trim() !== ""
        : String(r.corporate_unit_price).trim() !== "";
  const pricedList = Object.values(drafts).filter(isPriced);
  const excludedCount = pricedList.filter((r) => r.is_excluded).length;

  // A group- or classification-level rate saves as ONE code-based scope line —
  // scope_type CATEGORY for a service group, SUB_CATEGORY for a classification —
  // keyed by the group/classification *code* as scope_code (NOT service_code, NOT
  // the numeric id). The pricing resolver matches the most specific scope first,
  // so the per-service SERVICE_CODE exceptions below (priced or excluded) override
  // the group/classification rate.
  const groupCode = groups.find((g) => g.id === groupId)?.code || "";
  const groupName = groups.find((g) => g.id === groupId)?.name || "";
  const classification = classifications.find((c) => c.id === classificationId);
  const classificationCode = classification?.code || "";
  const classificationName = classification?.name || "";
  const bulkScopeReady = rateLevel === "GROUP" ? Boolean(groupId) : Boolean(groupId && classificationId);
  const bulkValueReady =
    bulk.is_excluded ||
    (bulk.pricing_method === "PERCENTAGE_DISCOUNT"
      ? String(bulk.discount_percentage).trim() !== ""
      : String(bulk.corporate_unit_price).trim() !== "");
  const bulkLineReady = rateLevel !== "SERVICE" && bulkScopeReady && bulkValueReady;
  const totalLines = (bulkLineReady ? 1 : 0) + pricedList.length;

  // Bed type + effective window stamped on every saved line (blank bed omitted = any bed).
  // The saved bed comes from the "This rate applies to" selector, OR — so the visible catalog
  // filter isn't silently ignored — from the SERVICE-level "Bed type" filter when that is the
  // only one set. Either dropdown therefore scopes the saved line.
  const lineScopeFields = () => {
    const f = { effective_from: lineEffFrom || effectiveFrom || todayISO() };
    const bed = lineBedType || bedTypeCode;
    if (bed) f.bed_type_code = bed;
    if (lineEffTo) f.effective_to = lineEffTo;
    return f;
  };

  const buildScopedItem = () =>
    rateLevel === "GROUP"
      ? {
          scope_type: "CATEGORY",
          scope_code: groupCode,
          scope_name: groupName,
          priority: 80, // least specific → highest number → loses to service lines
          ...lineScopeFields(),
          ...pricingFields(bulk),
        }
      : {
          scope_type: "SUB_CATEGORY",
          scope_code: classificationCode,
          scope_name: classificationName,
          priority: 50,
          ...lineScopeFields(),
          ...pricingFields(bulk),
        };

  const save = async () => {
    if (rateLevel !== "SERVICE" && bulkScopeReady && !bulkValueReady) {
      toast.error(`Set the ${rateLevel === "GROUP" ? "group" : "classification"} rate or mark it excluded`);
      return;
    }
    if (totalLines === 0) {
      toast.error(
        rateLevel === "SERVICE"
          ? "Price or exclude at least one service"
          : rateLevel === "GROUP" ? "Select a service group and set a rate" : "Select a classification and set a rate"
      );
      return;
    }
    if (bulkLineReady && !(rateLevel === "GROUP" ? groupCode : classificationCode)) {
      toast.error(`Missing ${rateLevel === "GROUP" ? "group" : "classification"} code — cannot save a scoped rate`);
      return;
    }
    setSaving(true);
    try {
      const items = [];
      if (bulkLineReady) items.push(buildScopedItem());
      pricedList.forEach((r) => {
        // Each exception carries THIS row's own bed type so ICU/GB/… price
        // independently; the "This rate applies to" bed only fills in when the
        // service itself is bed-agnostic (no per-row bed type).
        const scope = { ...lineScopeFields() };
        if (r.bed_type_code) scope.bed_type_code = r.bed_type_code;
        items.push({
          service_code: r.service_code,
          scope_type: "SERVICE_CODE",
          scope_name: r.scope_name,
          priority: 10, // most specific → lowest number → wins over group/classification
          ...scope,
          ...pricingFields(r),
        });
      });
      const res = await bulkSaveRateCardItems(rateCardId, {
        effective_from: lineEffFrom || effectiveFrom || todayISO(),
        dry_run: false,
        replace_existing: false,
        items,
      });
      const created = res.created_count ?? res.created?.length ?? items.length;
      const errCount = res.error_count ?? res.errors?.length ?? 0;
      const text = `Saved ${created} line(s)${errCount ? `, ${errCount} error(s)` : ""}`;
      if (errCount) toast.warning(text);
      else toast.success(text);
      setDrafts({});
      setBulk(emptyBulk());
      onSaved();
    } catch (err) {
      toast.error(toBillingError(err, "Could not save lines"));
    } finally {
      setSaving(false);
    }
  };

  const nf = nameFilter.trim().toLowerCase();
  const visibleServices = nf
    ? services.filter((s) => [s.name, s.code].filter(Boolean).join(" ").toLowerCase().includes(nf))
    : services;

  const isBulkLevel = rateLevel !== "SERVICE";

  return (
    <Box>
      <Box sx={{ marginBottom: "14px" }}>
        <IpdFormField label="Rate level">
          <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {RATE_LEVELS.map((lv) => (
              <IpdButton
                key={lv.id}
                size="small"
                variant={rateLevel === lv.id ? "contained" : "outlined"}
                onClick={() => setRateLevel(lv.id)}
              >
                {lv.label}
              </IpdButton>
            ))}
          </Box>
        </IpdFormField>
        <Box sx={{ marginTop: "6px", fontSize: "11px", color: C.textSub }}>
          {rateLevel === "SERVICE"
            ? "Price each service individually."
            : rateLevel === "CLASSIFICATION"
              ? "One rate saved at classification scope. Add per-service exceptions below — they override the classification rate."
              : "One rate saved at service-group scope. Add per-service exceptions below — they override the group rate."}
        </Box>
      </Box>

      {/* Cascade: group → classification → bed type (+ sample, name filter).
          Bed type narrows a group+classification down to one bed's tariff row —
          without it, a service like CBC lists every bed variant (GB/ICU/PR/SP/ITU)
          with different fees. At bulk levels the bed/sample narrowing is hidden
          (the scoped rate covers the whole group/classification regardless), but
          the name filter stays so individual exceptions are easy to find. */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "14px" }}>
        <IpdFormField label="Service group" required>
          <IpdTextField select fullWidth value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <MenuItem value="">Select group…</MenuItem>
            {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
        {rateLevel !== "GROUP" && (
          <IpdFormField label="Classification" required={rateLevel === "CLASSIFICATION"}>
            <IpdTextField select fullWidth value={classificationId} onChange={(e) => setClassificationId(e.target.value)} disabled={!groupId}>
              <MenuItem value="">
                {!groupId ? "Select group first" : rateLevel === "CLASSIFICATION" ? "Select classification…" : "All classifications"}
              </MenuItem>
              {classifications.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </IpdTextField>
          </IpdFormField>
        )}
        {!isBulkLevel && (
          <>
            <IpdFormField label="Sample type (labs)">
              <IpdTextField select fullWidth value={sampleId} onChange={(e) => setSampleId(e.target.value)} disabled={!groupId}>
                <MenuItem value="">All samples</MenuItem>
                {sampleTypes.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
          </>
        )}
        <IpdFormField label="Filter by name">
          <IpdTextField fullWidth value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} placeholder="chest, echo…" disabled={!groupId} />
        </IpdFormField>
      </Box>

      {/* This rate applies to — bed type + effective window stamped on every line saved in
          this batch. A bed-specific line (e.g. ICU) wins over a bed-agnostic one at the same
          scope; leave blank for "any bed". Dates must sit inside the rate card's window. */}
      <Box sx={{ padding: "12px 16px", backgroundColor: IPD_COLORS.pageBg, border: `1px solid ${C.border}`, borderRadius: "10px", marginBottom: "14px" }}>
        <Box sx={{ fontSize: "12px", fontWeight: 700, color: C.textSub, marginBottom: "10px" }}>This rate applies to</Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 260px))", justifyContent: "start", gap: "12px" }}>
          <IpdFormField label="Bed type (blank = any bed)">
            <IpdTextField select fullWidth value={lineBedType} onChange={(e) => setLineBedType(e.target.value)}>
              <MenuItem value="">Any bed type</MenuItem>
              {bedTypes.map((b) => <MenuItem key={b.id} value={b.code}>{b.name} ({b.code})</MenuItem>)}
            </IpdTextField>
          </IpdFormField>
          <IpdFormField label="Effective from">
            <IpdDateField value={lineEffFrom} onChange={(iso) => setLineEffFrom(iso)} />
          </IpdFormField>
          <IpdFormField label="Effective to (optional)">
            <IpdDateField value={lineEffTo} onChange={(iso) => setLineEffTo(iso)} />
          </IpdFormField>
        </Box>
      </Box>

      {/* Bulk rate for GROUP / CLASSIFICATION — saved as ONE code-based scope
          line (CATEGORY / SUB_CATEGORY). The service table below is only for
          per-service exceptions, which override the bulk rate. */}
      {isBulkLevel && (
        <Box sx={{ padding: "14px 16px", backgroundColor: bulkLineReady ? IPD_COLORS.info.bg : IPD_COLORS.pageBg, border: `1px solid ${bulkLineReady ? IPD_COLORS.info.border : C.border}`, borderRadius: "10px", marginBottom: "14px" }}>
          <Box sx={{ fontSize: "12px", fontWeight: 700, color: C.text, marginBottom: "10px" }}>
            {rateLevel === "GROUP" ? "Group rate" : "Classification rate"}
            {bulkScopeReady && (
              <Box component="span" sx={{ fontWeight: 500, color: C.textSub }}>
                {" — "}{rateLevel === "GROUP" ? (groupName || "selected group") : (classificationName || "selected classification")}
              </Box>
            )}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", alignItems: "end" }}>
            <IpdFormField label="Coverage">
              <IpdTextField
                select
                fullWidth
                value={bulk.is_excluded ? "EXCLUDED" : "INCLUDED"}
                onChange={(e) => setBulk((b) => ({ ...b, is_excluded: e.target.value === "EXCLUDED" }))}
              >
                <MenuItem value="INCLUDED">Included</MenuItem>
                <MenuItem value="EXCLUDED">Excluded</MenuItem>
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label="Method">
              <IpdTextField
                select
                fullWidth
                value={bulk.pricing_method}
                onChange={(e) => setBulk((b) => ({ ...b, pricing_method: e.target.value }))}
                disabled={bulk.is_excluded}
              >
                <MenuItem value="PERCENTAGE_DISCOUNT">% discount</MenuItem>
                <MenuItem value="FIXED_RATE">Fixed rate</MenuItem>
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label={bulk.pricing_method === "PERCENTAGE_DISCOUNT" ? "Discount %" : "Corporate price"}>
              {bulk.pricing_method === "PERCENTAGE_DISCOUNT" ? (
                <IpdTextField fullWidth type="number" value={bulk.discount_percentage} onChange={(e) => setBulk((b) => ({ ...b, discount_percentage: e.target.value }))} placeholder="20.0" disabled={bulk.is_excluded} />
              ) : (
                <IpdTextField fullWidth type="number" value={bulk.corporate_unit_price} onChange={(e) => setBulk((b) => ({ ...b, corporate_unit_price: e.target.value }))} placeholder="300.00" disabled={bulk.is_excluded} />
              )}
            </IpdFormField>
          </Box>
          <Box sx={{ marginTop: "10px", fontSize: "11px", color: C.textSub }}>
            {!bulkScopeReady
              ? (rateLevel === "GROUP" ? "Pick a service group above." : "Pick a group and classification above.")
              : !bulkValueReady
                ? "Set the rate or mark this scope excluded — it saves as one line covering the whole scope."
                : "Saves as one line for the whole scope. Per-service exceptions below take priority."}
          </Box>
        </Box>
      )}

      {isBulkLevel && groupId && (
        <Box sx={{ fontSize: "12px", fontWeight: 700, color: C.textSub, marginBottom: "8px" }}>
          Per-service exceptions (optional)
        </Box>
      )}
      {!groupId ? (
        <Box sx={{ padding: "16px", backgroundColor: IPD_COLORS.pageBg, border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "13px", color: C.textSub, marginBottom: "14px" }}>
          Pick a service group to list its services and set corporate prices.
        </Box>
      ) : (
        <TableContainer sx={{ border: `1px solid ${C.border}`, borderRadius: "10px", marginBottom: "14px", maxHeight: "440px" }}>
          <Table stickyHeader sx={{ minWidth: "860px" }}>
            <TableHead>
              <TableRow>
                {["Unique code", "Service", "Bed", "Rack", "Coverage", "Method", "Corporate price / %"].map((h) => (
                  <TableCell key={h} sx={{ padding: "10px 14px", fontSize: "11px", fontWeight: 700, color: C.textSub, textTransform: "uppercase", whiteSpace: "nowrap", backgroundColor: IPD_COLORS.pageBg }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {servicesLoading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ padding: "22px", color: C.textSub, fontSize: "13px" }}>Loading services…</TableCell></TableRow>
              ) : visibleServices.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ padding: "22px", color: C.textSub, fontSize: "13px" }}>No services in this scope.</TableCell></TableRow>
              ) : (
                visibleServices.map((s) => {
                  const key = draftKey(s);
                  const d = key ? drafts[key] : null;
                  const method = d?.pricing_method || "FIXED_RATE";
                  const excluded = Boolean(d?.is_excluded);
                  const priced = d && isPriced(d);
                  const rowBg = excluded ? IPD_COLORS.warning.bg : priced ? IPD_COLORS.info.bg : "transparent";
                  return (
                    <TableRow key={s.id} sx={{ backgroundColor: rowBg }}>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "13px", fontWeight: 700, color: C.primary, whiteSpace: "nowrap" }}>{s.uniqueCode || "—"}</TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "13px", color: C.text }}>
                        {s.name}
                        <Box sx={{ fontSize: "11px", color: C.textSub, fontFamily: "monospace" }}>{s.code || "—"}</Box>
                      </TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{s.bedTypeCode || "—"}</TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "13px", color: C.textSub, whiteSpace: "nowrap" }}>{s.fee != null ? fmtINR(s.fee) : "—"}</TableCell>
                      <TableCell sx={{ padding: "8px 14px" }}>
                        <IpdTextField
                          select
                          value={excluded ? "EXCLUDED" : "INCLUDED"}
                          onChange={(e) => setDraft(s, { is_excluded: e.target.value === "EXCLUDED" })}
                          disabled={!key}
                          sx={{ width: "auto", minWidth: 128 }}
                        >
                          <MenuItem value="INCLUDED">Included</MenuItem>
                          <MenuItem value="EXCLUDED">Excluded</MenuItem>
                        </IpdTextField>
                      </TableCell>
                      <TableCell sx={{ padding: "8px 14px" }}>
                        <IpdTextField select value={method} onChange={(e) => setDraft(s, { pricing_method: e.target.value })} disabled={!key || excluded} sx={{ width: "auto", minWidth: 140 }}>
                          <MenuItem value="FIXED_RATE">Fixed rate</MenuItem>
                          <MenuItem value="PERCENTAGE_DISCOUNT">% discount</MenuItem>
                        </IpdTextField>
                      </TableCell>
                      <TableCell sx={{ padding: "8px 14px" }}>
                        {excluded ? (
                          <Box component="span" sx={{ fontSize: "12px", fontWeight: 600, color: IPD_COLORS.warning.text, whiteSpace: "nowrap" }}>
                            Not covered — rack rate
                          </Box>
                        ) : method === "PERCENTAGE_DISCOUNT" ? (
                          <IpdTextField type="number" value={d?.discount_percentage ?? ""} onChange={(e) => setDraft(s, { discount_percentage: e.target.value })} placeholder="20.0" disabled={!key} sx={{ width: "130px" }} />
                        ) : (
                          <IpdTextField type="number" value={d?.corporate_unit_price ?? ""} onChange={(e) => setDraft(s, { corporate_unit_price: e.target.value })} placeholder="300.00" disabled={!key} sx={{ width: "130px" }} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        <IpdButton onClick={save} disabled={saving || totalLines === 0} loading={saving}>
          {`Save ${totalLines || ""} line(s)`}
        </IpdButton>
        {(pricedList.length > 0 || bulkLineReady) && (
          <>
            {pricedList.length > 0 && (
              <IpdButton variant="outlined" onClick={() => setDrafts({})}>Clear exceptions</IpdButton>
            )}
            <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>
              {bulkLineReady && `1 ${rateLevel === "GROUP" ? "group" : "classification"} line`}
              {bulkLineReady && pricedList.length > 0 && " + "}
              {pricedList.length > 0 &&
                `${pricedList.length - excludedCount} priced, ${excludedCount} excluded exception(s)`}
              {" — save applies all."}
            </Box>
          </>
        )}
      </Box>
      <Box sx={{ marginTop: "8px", fontSize: "11px", color: C.textSub }}>
        Each bed type prices independently — a row's rate applies only to its own bed tariff.
      </Box>
    </Box>
  );
}

// ── CSV / Excel upload ────────────────────────────────────────────────────────
function CsvUpload({ rateCardId, effectiveFrom, onSaved }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    setBusy("template");
    try {
      const csv = await getImportTemplate();
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "corporate-rate-card-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(toBillingError(err, "Could not download template"));
    } finally {
      setBusy("");
    }
  };

  const run = async (dryRun) => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(dryRun ? "validate" : "import"); setResult(null);
    try {
      const res = await importRateCardItems(rateCardId, file, {
        dryRun,
        replaceExisting,
        effectiveFrom: effectiveFrom || undefined,
      });
      setResult({ ...res, dry_run: dryRun });
      const created = res.created_count ?? res.created?.length ?? 0;
      const errCount = res.error_count ?? res.errors?.length ?? 0;
      if (dryRun) toast.info(`Validated: ${created} ready${errCount ? `, ${errCount} error(s)` : ""}`);
      else if (errCount) toast.warning(`Imported ${created} line(s), ${errCount} error(s)`);
      else toast.success(`Imported ${created} line(s)`);
      if (!dryRun) onSaved();
    } catch (err) {
      toast.error(toBillingError(err, "Import failed"));
    } finally {
      setBusy("");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center", marginBottom: "14px" }}>
        <IpdButton variant="outlined" onClick={downloadTemplate} loading={busy === "template"} startIcon={<SvgIcon name="download" size={15} color={C.text} />}>
          Download template
        </IpdButton>
        <IpdButton variant="outlined" onClick={() => fileRef.current?.click()} startIcon={<SvgIcon name="upload" size={15} color={C.text} />}>
          {file ? file.name : "Choose file"}
        </IpdButton>
        <Box
          component="input"
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
          sx={{ display: "none" }}
        />
      </Box>

      <FormControlLabel
        control={<Checkbox size="small" checked={replaceExisting} onChange={(e) => setReplaceExisting(e.target.checked)} />}
        label="Replace existing active lines before import"
        sx={{ display: "flex", mx: 0, mb: "14px", "& .MuiFormControlLabel-label": { fontSize: "13px", color: C.text } }}
      />

      <Box sx={{ display: "flex", gap: "10px" }}>
        <IpdButton variant="outlined" onClick={() => run(true)} disabled={!file || Boolean(busy)} loading={busy === "validate"}>
          Validate (dry run)
        </IpdButton>
        <IpdButton onClick={() => run(false)} disabled={!file || Boolean(busy)} loading={busy === "import"}>
          Import
        </IpdButton>
      </Box>

      {result && (
        <Box sx={{ marginTop: "16px", padding: "14px 16px", backgroundColor: IPD_COLORS.pageBg, border: `1px solid ${C.border}`, borderRadius: "10px" }}>
          <Box sx={{ fontSize: "13px", fontWeight: 700, color: C.text, marginBottom: "8px" }}>
            {result.dry_run ? "Validation" : "Import"} result
          </Box>
          <Box sx={{ display: "flex", gap: "18px", fontSize: "13px", color: C.textSub, flexWrap: "wrap" }}>
            <Box component="span">Rows: <Box component="b" sx={{ color: C.text }}>{result.total_rows ?? "—"}</Box></Box>
            <Box component="span">Created: <Box component="b" sx={{ color: C.success }}>{result.created_count ?? result.created?.length ?? 0}</Box></Box>
            <Box component="span">Errors: <Box component="b" sx={{ color: (result.error_count ?? result.errors?.length ?? 0) ? C.danger : C.text }}>{result.error_count ?? result.errors?.length ?? 0}</Box></Box>
          </Box>
          {Array.isArray(result.errors) && result.errors.length > 0 && (
            <Box sx={{ marginTop: "10px", maxHeight: "160px", overflowY: "auto" }}>
              {result.errors.map((e, i) => (
                <Box key={i} sx={{ fontSize: "12px", color: C.danger, padding: "3px 0" }}>
                  Row {e.row ?? "?"}: {e.message || e.error || "error"}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── Inline edit for a saved line (bed type, effective dates, coverage, method, price) ──
// Uses POST .../items/{id}/update/ (platform is GET/POST only). Scope/code are the line's
// identity and stay fixed — edit them by deactivating and re-adding.
function EditItemForm({ rateCardId, item, bedTypes, onSaved, onClose }) {
  const [form, setForm] = useState({
    bed_type_code: item.bedType || "",
    effective_from: item.effectiveFrom || todayISO(),
    effective_to: item.effectiveTo || "",
    is_excluded: Boolean(item.excluded),
    pricing_method: item.pricingMethod === "PERCENTAGE_DISCOUNT" ? "PERCENTAGE_DISCOUNT" : "FIXED_RATE",
    corporate_unit_price: item.corporateUnitPrice != null ? String(item.corporateUnitPrice) : "",
    discount_percentage: item.discountPercentage != null ? String(item.discountPercentage) : "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const body = {
      bed_type_code: form.bed_type_code || null,
      effective_from: form.effective_from,
      effective_to: form.effective_to || null,
    };
    if (form.is_excluded) {
      body.pricing_method = "EXCLUDE";
    } else if (form.pricing_method === "PERCENTAGE_DISCOUNT") {
      body.pricing_method = "PERCENTAGE_DISCOUNT";
      body.discount_percentage = String(form.discount_percentage);
    } else {
      body.pricing_method = "FIXED_RATE";
      body.corporate_unit_price = String(form.corporate_unit_price);
    }
    try {
      await updateRateCardItem(rateCardId, item.id, body);
      toast.success(`Line ${item.scopeCode || ""} updated`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(toBillingError(err, "Could not update line"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <IpdSectionCard
      title={`Edit line — ${item.scopeCode || item.scopeName || ""}`}
      subtitle={`${item.scopeType} · identity (scope/code) is fixed`}
      actions={
        <Box sx={{ display: "flex", gap: "8px" }}>
          <IpdButton variant="outlined" onClick={onClose}>Cancel</IpdButton>
          <IpdButton onClick={save} loading={saving}>Save line</IpdButton>
        </Box>
      }
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", alignItems: "end" }}>
        <IpdFormField label="Bed type (blank = any bed)">
          <IpdTextField select fullWidth value={form.bed_type_code} onChange={(e) => setForm((f) => ({ ...f, bed_type_code: e.target.value }))}>
            <MenuItem value="">Any bed type</MenuItem>
            {bedTypes.map((b) => <MenuItem key={b.id} value={b.code}>{b.name} ({b.code})</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label="Effective from">
          <IpdDateField value={form.effective_from} onChange={(iso) => setForm((f) => ({ ...f, effective_from: iso }))} />
        </IpdFormField>
        <IpdFormField label="Effective to (optional)">
          <IpdDateField value={form.effective_to} onChange={(iso) => setForm((f) => ({ ...f, effective_to: iso }))} />
        </IpdFormField>
        <IpdFormField label="Coverage">
          <IpdTextField select fullWidth value={form.is_excluded ? "EXCLUDED" : "INCLUDED"} onChange={(e) => setForm((f) => ({ ...f, is_excluded: e.target.value === "EXCLUDED" }))}>
            <MenuItem value="INCLUDED">Included</MenuItem>
            <MenuItem value="EXCLUDED">Excluded</MenuItem>
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label="Method">
          <IpdTextField select fullWidth value={form.pricing_method} onChange={(e) => setForm((f) => ({ ...f, pricing_method: e.target.value }))} disabled={form.is_excluded}>
            <MenuItem value="PERCENTAGE_DISCOUNT">% discount</MenuItem>
            <MenuItem value="FIXED_RATE">Fixed rate</MenuItem>
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label={form.pricing_method === "PERCENTAGE_DISCOUNT" ? "Discount %" : "Corporate price"}>
          {form.pricing_method === "PERCENTAGE_DISCOUNT" ? (
            <IpdTextField fullWidth type="number" value={form.discount_percentage} onChange={(e) => setForm((f) => ({ ...f, discount_percentage: e.target.value }))} disabled={form.is_excluded} placeholder="20.0" />
          ) : (
            <IpdTextField fullWidth type="number" value={form.corporate_unit_price} onChange={(e) => setForm((f) => ({ ...f, corporate_unit_price: e.target.value }))} disabled={form.is_excluded} placeholder="300.00" />
          )}
        </IpdFormField>
      </Box>
    </IpdSectionCard>
  );
}

// ── Existing items table ──────────────────────────────────────────────────────
function ExistingItems({ items, loading, onDeactivate, onEdit, busyId }) {
  const columns = [
    { field: "scopeCode", headerName: "Code", flex: 1.4, minWidth: 240, sortable: false,
      renderCell: (p) => (
        <Box component="span" title={p.value || ""} sx={{ fontSize: "13px", fontWeight: 600, color: C.text, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {p.value || "—"}
        </Box>
      ) },
    { field: "scopeName", headerName: "Name", flex: 1.4, minWidth: 150, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.text }}>{p.value || "—"}</Box> },
    { field: "bedType", headerName: "Bed type", flex: 0.8, minWidth: 90, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{p.value || "Any"}</Box> },
    { field: "coverage", headerName: "Coverage", flex: 0.6, minWidth: 90, sortable: false,
      renderCell: (p) => (
        <Box component="span" sx={{ fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap", color: p.row.excluded ? IPD_COLORS.warning.text : IPD_COLORS.success.text }}>
          {p.row.excluded ? "Excluded" : "Included"}
        </Box>
      ) },
    { field: "pricingMethod", headerName: "Method", flex: 1.5, minWidth: 170, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{p.row.excluded ? "—" : p.value}</Box> },
    { field: "corporate", headerName: "Corporate", flex: 1, minWidth: 120, sortable: false,
      renderCell: (p) => (
        <Box component="span" sx={{ fontSize: "13px", color: C.text, whiteSpace: "nowrap" }}>
          {p.row.excluded
            ? "Rack rate"
            : p.row.pricingMethod === "PERCENTAGE_DISCOUNT"
              ? `${p.row.discountPercentage ?? "—"}%`
              : p.row.corporateUnitPrice != null ? fmtINR(p.row.corporateUnitPrice) : "—"}
        </Box>
      ) },
    { field: "effectiveFrom", headerName: "Effective from", flex: 0.9, minWidth: 120, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{fmtDate(p.value)}</Box> },
    { field: "effectiveTo", headerName: "Effective to", flex: 0.9, minWidth: 120, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{fmtDate(p.value)}</Box> },
    { field: "actions", headerName: "", width: 92, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: "2px" }}>
          <IconButton size="small" onClick={() => onEdit(p.row)} disabled={busyId === p.row.id} title="Edit">
            <SvgIcon name="edit" size={16} color={C.primary} />
          </IconButton>
          <IconButton size="small" onClick={() => onDeactivate(p.row)} disabled={busyId === p.row.id} title="Deactivate">
            <SvgIcon name="trash" size={16} color={C.danger} />
          </IconButton>
        </Box>
      ) },
  ];

  return (
    <IpdDataGrid
      embedded
      hideFooter
      disableColumnMenu
      loading={loading}
      rows={items}
      columns={columns}
      emptyMessage="No line items yet — add via the grid or CSV upload."
    />
  );
}

// ── Editor root ───────────────────────────────────────────────────────────────
export default function CorporateRateCardEditor({ rateCardId: initialId, initialPayer, onBack, onSaved }) {
  const [rateCardId, setRateCardId] = useState(initialId);
  const [payer, setPayer] = useState(initialPayer);
  const [payers, setPayers] = useState([]);
  const [tab, setTab] = useState("manual"); // manual | upload
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [bedTypes, setBedTypes] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const effectiveFrom = todayISO();

  useEffect(() => {
    (async () => {
      try { setPayers(await listCorporatePayers({ limit: 100 })); } catch { /* non-fatal */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try { setBedTypes((await listLookups({ activeOnly: true })).bedTypes); } catch { /* non-fatal */ }
    })();
  }, []);

  const loadItems = useCallback(async () => {
    if (!rateCardId) return;
    setItemsLoading(true);
    try {
      setItems(await listRateCardItems(rateCardId));
    } catch {
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  }, [rateCardId]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleHeaderSaved = (newId, savedPayer) => {
    setRateCardId(newId);
    if (savedPayer) setPayer(savedPayer);
    if (onSaved) onSaved(newId, savedPayer); // keep parent view in sync
  };

  const deactivate = async (item) => {
    setBusyId(item.id);
    try {
      await deleteRateCardItem(rateCardId, item.id);
      toast.success(`Line ${item.scopeCode || ""} removed`);
      await loadItems();
    } catch (err) {
      toast.error(toBillingError(err, "Could not remove line"));
    } finally {
      setBusyId(null);
    }
  };

  const tabBtn = (id, label) => (
    <IpdButton
      key={id}
      variant={tab === id ? "contained" : "outlined"}
      size="small"
      onClick={() => setTab(id)}
    >
      {label}
    </IpdButton>
  );

  return (
    <Box sx={{ animation: "ipdFadeIn 0.2s ease" }}>
      <HeaderForm
        rateCardId={rateCardId}
        initialPayer={payer}
        payers={payers}
        onSaved={handleHeaderSaved}
      />

      {!rateCardId ? (
        <Box sx={{ padding: "16px 20px", backgroundColor: IPD_COLORS.info.bg, border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "13px", color: C.textSub }}>
          Save the header above to start adding line items.
        </Box>
      ) : (
        <>
          <IpdSectionCard
            title="Line items"
            subtitle="One rule per service, category, or bulk scope. More specific scope wins; lower priority breaks ties."
            actions={<Box sx={{ display: "flex", gap: "8px" }}>{tabBtn("manual", "Manual grid")}{tabBtn("upload", "CSV / Excel")}</Box>}
          >
            {tab === "manual" ? (
              <ManualGrid rateCardId={rateCardId} effectiveFrom={effectiveFrom} onSaved={loadItems} />
            ) : (
              <CsvUpload rateCardId={rateCardId} effectiveFrom={effectiveFrom} onSaved={loadItems} />
            )}
          </IpdSectionCard>

          {editItem && (
            <EditItemForm
              rateCardId={rateCardId}
              item={editItem}
              bedTypes={bedTypes}
              onSaved={loadItems}
              onClose={() => setEditItem(null)}
            />
          )}

          <IpdSectionCard title={`Saved lines (${items.length})`} subtitle="Active line items on this rate card">
            <ExistingItems items={items} loading={itemsLoading} onDeactivate={deactivate} onEdit={setEditItem} busyId={busyId} />
          </IpdSectionCard>
        </>
      )}

      <Box sx={{ marginTop: "8px" }}>
        <IpdButton variant="outlined" onClick={onBack} startIcon={<SvgIcon name="back" size={15} color={C.text} />}>
          Back to rate cards
        </IpdButton>
      </Box>
    </Box>
  );
}
